import React, { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, message } from "antd";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import APIService from "../../Service/APIService";
import ModalView from "../../common/modal/index.js";
import TextInput from "../../common/text-input/index.js";
import DatePickerComponent from "../../common/date-picker/index.js";
import Text from "../../common/typography/text.js";
import CommonButton from "../../common/button/button.js";
import InvoiceProductDetailsSection from "../InvoiceForm/InvoiceProductDetailsSection.js";

const normalizeDateValue = (value) =>
  value === null || value === undefined ? null : dayjs(value);

const DUPLICATE_TISSUE_ID_MESSAGE = "Duplicate Tissue ID in this order";
const REQUIRED_TISSUE_ID_MESSAGE = "Tissue ID is required";
const TISSUE_ID_CHECK_ERROR_MESSAGE = "Error checking Tissue ID";

const normalizeTissueId = (value) => String(value ?? "").trim().toUpperCase();

const isPositiveId = (value) => {
  const id = Number(value);
  return Number.isFinite(id) && id > 0;
};

const AddTrackingModal = ({ isOpen, onClose, orderType, orderData }) => {
  const [trackingNumber, setTrackingNumber] = useState(
    orderData?.TrackingNumber ?? ""
  );
  const [productShipDate, setProductShipDate] = useState(null);
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [serializedProducts, setSerializedProducts] = useState([]);
  const [nonSerializedProducts, setNonSerializedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const [requiredTissueErrors, setRequiredTissueErrors] = useState({});
  const [localDuplicateErrors, setLocalDuplicateErrors] = useState({});
  const [serverTissueErrors, setServerTissueErrors] = useState({});
  const [hasPendingTissueChecks, setHasPendingTissueChecks] = useState(false);
  const pendingChecksRef = useRef(new Set());
  const scheduledChecksRef = useRef({});
  const latestNormalizedTissueIdsRef = useRef({});
  const tissueDuplicateCheckCacheRef = useRef(new Map());
  const apiServ = new APIService();

  const combinedTissueErrors = useMemo(() => {
    const rowKeys = new Set([
      ...Object.keys(requiredTissueErrors),
      ...Object.keys(localDuplicateErrors),
      ...Object.keys(serverTissueErrors),
    ]);
    const errors = {};

    rowKeys.forEach((rowKey) => {
      if (requiredTissueErrors[rowKey]) {
        errors[rowKey] = requiredTissueErrors[rowKey];
        return;
      }
      if (serverTissueErrors[rowKey]) {
        errors[rowKey] = serverTissueErrors[rowKey];
        return;
      }
      if (localDuplicateErrors[rowKey]) {
        errors[rowKey] = localDuplicateErrors[rowKey];
      }
    });

    return errors;
  }, [requiredTissueErrors, localDuplicateErrors, serverTissueErrors]);

  const mergeStagedLineDetails = (responseData) => {
    const productLineDetails = responseData?.ProductLineDetails ?? [];
    return new Map(
      productLineDetails.map((item) => [
        `${item.OrderProductID}:${item.LineIndex}`,
        item,
      ])
    );
  };

  const buildOrderProductIdentityResolver = (responseData) => {
    const stagedByInvoiceProductId = new Map(
      (responseData?.ProductLineDetails ?? [])
        .filter((item) => isPositiveId(item.InvoiceProductID))
        .map((item) => [Number(item.InvoiceProductID), item])
    );
    const candidates = [
      ...(responseData?.SerializedProducts ?? []),
      ...(responseData?.NonSerializedProducts ?? []),
    ].flatMap((item) => {
      const qty = Number(item.Quantity);
      const lineCount = Number.isFinite(qty) && qty > 0 ? qty : 1;
      return Array.from({ length: lineCount }, (_, lineIndex) => ({
        orderProductId: Number(item.ID),
        productId: Number(item.ProductID),
        productCode: item.ProductCode,
        price: Number(item.Price),
        lineIndex,
        used: false,
      }));
    });

    return (invoiceProduct) => {
      const stagedLine = stagedByInvoiceProductId.get(Number(invoiceProduct.ID));
      const requestedOrderProductId = isPositiveId(
        stagedLine?.OrderProductID ?? invoiceProduct.OrderProductID
      )
        ? Number(stagedLine?.OrderProductID ?? invoiceProduct.OrderProductID)
        : null;
      const requestedLineIndex =
        stagedLine?.LineIndex !== undefined && stagedLine?.LineIndex !== null
          ? Number(stagedLine.LineIndex)
          : null;
      const productId = Number(invoiceProduct.ProductID);

      let candidate = candidates.find(
        (item) =>
          !item.used &&
          requestedOrderProductId === item.orderProductId &&
          requestedLineIndex === item.lineIndex
      );
      candidate ??= candidates.find(
        (item) =>
          !item.used &&
          requestedOrderProductId === item.orderProductId &&
          item.productId === productId
      );
      candidate ??= candidates.find(
        (item) =>
          !item.used &&
          item.productId === productId &&
          item.price === Number(invoiceProduct.Price)
      );
      candidate ??= candidates.find(
        (item) => !item.used && item.productId === productId
      );
      candidate ??= candidates.find(
        (item) =>
          !item.used &&
          invoiceProduct.ProductCode &&
          item.productCode === invoiceProduct.ProductCode
      );

      if (!candidate) {
        throw new Error(
          `Invoice product ${invoiceProduct.ProductCode ?? invoiceProduct.ID} cannot be matched to this order`
        );
      }

      candidate.used = true;
      return candidate;
    };
  };

  const buildRowsFromInvoiceProducts = (
    products,
    serialized,
    startKey,
    resolveOrderProductIdentity
  ) => {
    let key = startKey;
    const rows = (products ?? []).map((item) => {
      const identity = resolveOrderProductIdentity(item);
      return {
        ID: item.ID,
        InvoiceProductID: item.ID,
        TissueID: item.TissueID ? item.TissueID : "",
        ProductID: item.ProductID,
        ProductCode: item.ProductCode,
        ProductName: item.ProductName,
        ProductTotal: item.ProductTotal,
        Price: item.Price,
        OrderProductID: identity.orderProductId,
        AccountID: orderData.AccountID,
        AccountName: orderData.AccountName,
        ManufacturerID: orderData.ManufacturerID,
        ManufacturerName: orderData.ManufacturerName,
        OrderID: orderData.ID,
        OrderType: orderType,
        ExpirationDate: normalizeDateValue(item.ExpirationDate),
        IsUsed: item.IsUsed,
        Serialized: serialized,
        ShipDate: normalizeDateValue(item.ShipDate),
        LineIndex: identity.lineIndex,
        key: key++,
      };
    });
    return { rows, nextKey: key };
  };

  const buildRowsFromOrderProducts = (products, serialized, stagedLineByKey, startKey) => {
    const rows = [];
    let key = startKey;
    (products ?? []).forEach((item) => {
      const qty = Number(item.Quantity);
      const lineCount = Number.isFinite(qty) && qty > 0 ? qty : 1;
      for (let i = 0; i < lineCount; i += 1) {
        const stagedLine = stagedLineByKey.get(`${item.ID}:${i}`);
        rows.push({
          ID: item.ID,
          TissueID:
            stagedLine?.TissueID ?? (item.TissueID ? item.TissueID : ""),
          ProductID: item.ProductID,
          ProductCode: item.ProductCode,
          ProductName: item.ProductName,
          ProductTotal: item.ProductTotal,
          Price: stagedLine?.Price ?? item.Price,
          OrderProductID: item.ID,
          AccountID: orderData.AccountID,
          AccountName: orderData.AccountName,
          ManufacturerID: orderData.ManufacturerID,
          ManufacturerName: orderData.ManufacturerName,
          OrderID: orderData.ID,
          OrderType: orderType,
          ExpirationDate: normalizeDateValue(
            stagedLine?.ExpirationDate ?? item.ExpirationDate
          ),
          IsUsed: stagedLine?.IsUsed ?? item.IsUsed,
          Serialized: serialized,
          ShipDate: normalizeDateValue(stagedLine?.ShipDate ?? item.ShipDate),
          LineIndex: i,
          key: key++,
        });
      }
    });
    return { rows, nextKey: key };
  };

  const removeRowError = (errors, rowKey) => {
    if (!(rowKey in errors)) {
      return errors;
    }
    const nextErrors = { ...errors };
    delete nextErrors[rowKey];
    return nextErrors;
  };

  const checkRequiredTissueIds = (data) =>
    (data ?? []).reduce((errors, row) => {
      if (!normalizeTissueId(row.TissueID)) {
        errors[row.key] = REQUIRED_TISSUE_ID_MESSAGE;
      }
      return errors;
    }, {});

  const checkLocalDuplicates = (data) => {
    const rowKeysByTissueId = {};
    const errors = {};

    (data ?? []).forEach((row) => {
      const normalizedTissueId = normalizeTissueId(row.TissueID);
      if (!normalizedTissueId) {
        return;
      }
      if (!rowKeysByTissueId[normalizedTissueId]) {
        rowKeysByTissueId[normalizedTissueId] = [];
      }
      rowKeysByTissueId[normalizedTissueId].push(row.key);
    });

    Object.values(rowKeysByTissueId).forEach((rowKeys) => {
      if (rowKeys.length < 2) {
        return;
      }
      rowKeys.forEach((rowKey) => {
        errors[rowKey] = DUPLICATE_TISSUE_ID_MESSAGE;
      });
    });

    return errors;
  };

  const buildDuplicateMessage = (result) => {
    if (result?.type === "invoice-duplicate") {
      const invoice = result.invoice;
      return (
        <>
          Duplicate Tissue ID found in Invoice Number{" "}
          <a
            href={`/InvoiceHome/${invoice?.ID}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {invoice?.InvoiceNumber ?? invoice?.ID}
          </a>
        </>
      );
    }

    if (result?.type === "staged-duplicate") {
      return "Duplicate Tissue ID found in another shipped order line";
    }

    if (result?.type === "request-error") {
      return TISSUE_ID_CHECK_ERROR_MESSAGE;
    }

    return null;
  };

  const buildTissueCheckQuery = (row, tissueId) => {
    const params = new URLSearchParams({
      TissueID: tissueId.trim(),
      OrderID: String(orderData.ID),
      OrderType: String(orderType ?? ""),
    });

    if (invoiceId) {
      params.set("InvoiceID", String(invoiceId));
    }
    if (row?.InvoiceProductID) {
      params.set("InvoiceProductID", String(row.InvoiceProductID));
    }
    if (row?.OrderProductID) {
      params.set("OrderProductID", String(row.OrderProductID));
    }
    if (row?.LineIndex !== undefined && row?.LineIndex !== null) {
      params.set("LineIndex", String(row.LineIndex));
    }

    return params.toString();
  };

  const fetchTissueDuplicateInfo = async (row) => {
    const trimmedValue = String(row?.TissueID ?? "").trim();
    const normalizedValue = normalizeTissueId(trimmedValue);

    if (!normalizedValue) {
      return null;
    }

    const cacheKey = [
      normalizedValue,
      invoiceId ?? "",
      row?.InvoiceProductID ?? "",
      row?.OrderProductID ?? "",
      row?.LineIndex ?? "",
    ].join(":");

    if (!tissueDuplicateCheckCacheRef.current.has(cacheKey)) {
      const requestPromise = apiServ
        .getDataFromBackend(
          `CheckForDuplicateTissueID?${buildTissueCheckQuery(row, trimmedValue)}`
        )
        .then((res) => {
          if (res?.data?.source === "invoice" && res?.data?.invoice?.ID) {
            return {
              type: "invoice-duplicate",
              invoice: res.data.invoice,
            };
          }
          if (res?.data?.source === "staged") {
            return {
              type: "staged-duplicate",
            };
          }
          return null;
        })
        .catch(() => {
          tissueDuplicateCheckCacheRef.current.delete(cacheKey);
          return {
            type: "request-error",
          };
        });

      tissueDuplicateCheckCacheRef.current.set(cacheKey, requestPromise);
    }

    return tissueDuplicateCheckCacheRef.current.get(cacheKey);
  };

  const syncPendingTissueChecks = () => {
    setHasPendingTissueChecks(pendingChecksRef.current.size > 0);
  };

  const clearScheduledTissueCheck = (rowKey) => {
    const scheduledCheck = scheduledChecksRef.current[rowKey];
    if (!scheduledCheck) {
      return;
    }

    clearTimeout(scheduledCheck.timeoutId);
    scheduledCheck.resolve();
    pendingChecksRef.current.delete(scheduledCheck.pendingPromise);
    delete scheduledChecksRef.current[rowKey];
    syncPendingTissueChecks();
  };

  const applyServerTissueError = (rowKey, result) => {
    setServerTissueErrors((prev) => {
      const message = buildDuplicateMessage(result);
      if (message) {
        return {
          ...prev,
          [rowKey]: message,
        };
      }

      return removeRowError(prev, rowKey);
    });
  };

  const scheduleTissueIdCheck = (row) => {
    const normalizedValue = normalizeTissueId(row?.TissueID);

    latestNormalizedTissueIdsRef.current[row.key] = normalizedValue;
    clearScheduledTissueCheck(row.key);

    if (!normalizedValue) {
      setServerTissueErrors((prev) => removeRowError(prev, row.key));
      return;
    }

    let resolvePendingCheck;
    const pendingPromise = new Promise((resolve) => {
      resolvePendingCheck = resolve;
    });

    pendingChecksRef.current.add(pendingPromise);
    syncPendingTissueChecks();

    const timeoutId = setTimeout(async () => {
      try {
        const result = await fetchTissueDuplicateInfo(row);
        if (latestNormalizedTissueIdsRef.current[row.key] === normalizedValue) {
          applyServerTissueError(row.key, result);
        }
      } finally {
        pendingChecksRef.current.delete(pendingPromise);
        delete scheduledChecksRef.current[row.key];
        resolvePendingCheck();
        syncPendingTissueChecks();
      }
    }, 350);

    scheduledChecksRef.current[row.key] = {
      pendingPromise,
      resolve: resolvePendingCheck,
      timeoutId,
    };
  };

  const waitForAllTissueChecks = async () => {
    await Promise.all([...pendingChecksRef.current]);
  };

  const validateTissueIdsBeforeSubmit = async () => {
    await waitForAllTissueChecks();

    const requiredErrors = checkRequiredTissueIds(serializedProducts);
    const localErrors = checkLocalDuplicates(serializedProducts);
    setRequiredTissueErrors(requiredErrors);
    setLocalDuplicateErrors(localErrors);

    const rowsWithoutLocalErrors = serializedProducts.filter(
      (row) => !requiredErrors[row.key] && !localErrors[row.key]
    );
    const serverValidationResults = await Promise.all(
      rowsWithoutLocalErrors.map(async (row) => ({
        rowKey: row.key,
        result: await fetchTissueDuplicateInfo(row),
      }))
    );

    const nextServerErrors = {};
    serverValidationResults.forEach(({ rowKey, result }) => {
      const message = buildDuplicateMessage(result);
      if (message) {
        nextServerErrors[rowKey] = message;
      }
    });
    setServerTissueErrors(nextServerErrors);

    return (
      Object.keys(requiredErrors).length === 0 &&
      Object.keys(localErrors).length === 0 &&
      Object.keys(nextServerErrors).length === 0
    );
  };

  const handleInputChange = (event, rowKey, column) => {
    const value = event.target.value;
    const existingRow = serializedProducts.find((row) => row.key === rowKey);
    const updatedRow = existingRow ? { ...existingRow, [column]: value } : null;

    setSerializedProducts((prev) =>
      prev.map((row) => (row.key === rowKey ? { ...row, [column]: value } : row))
    );

    if (column === "TissueID") {
      setRequiredTissueErrors((prev) => removeRowError(prev, rowKey));
      setServerTissueErrors((prev) => removeRowError(prev, rowKey));
      if (updatedRow) {
        scheduleTissueIdCheck(updatedRow);
      }
    }
  };

  const loadProducts = async () => {
    setIsLoading(true);
    try {
      const invoiceResponse = await apiServ.getDataFromBackend(
        `getinvoicedata?OrderID=${orderData.ID}&OrderType=${orderType}`
      );
      const invoice = invoiceResponse?.data?.data;

      if (invoice) {
        setInvoiceId(invoice.ID);
        const [invoiceProductResponse, orderProductResponse] = await Promise.all([
          apiServ.getDataFromBackend(`getinvoiceproucts?InvoiceID=${invoice.ID}`),
          apiServ.getDataFromBackend(
            `getorderproducts?orderID=${orderData.ID}&orderType=${orderType}`
          ),
        ]);
        const data = invoiceProductResponse?.data?.data;
        const resolveOrderProductIdentity = buildOrderProductIdentityResolver(
          orderProductResponse?.data?.data
        );
        const serializedResult = buildRowsFromInvoiceProducts(
          data?.SerializedProducts,
          true,
          1,
          resolveOrderProductIdentity
        );
        const nonSerializedResult = buildRowsFromInvoiceProducts(
          data?.NonSerializedProducts,
          false,
          serializedResult.nextKey,
          resolveOrderProductIdentity
        );
        setSerializedProducts(serializedResult.rows);
        setNonSerializedProducts(nonSerializedResult.rows);
        return;
      }

      setInvoiceId(null);
      const productResponse = await apiServ.getDataFromBackend(
        `getorderproducts?orderID=${orderData.ID}&orderType=${orderType}`
      );
      const data = productResponse?.data?.data;
      const stagedLineByKey = mergeStagedLineDetails(data);
      const serializedResult = buildRowsFromOrderProducts(
        data?.SerializedProducts,
        true,
        stagedLineByKey,
        1
      );
      const nonSerializedResult = buildRowsFromOrderProducts(
        data?.NonSerializedProducts,
        false,
        stagedLineByKey,
        serializedResult.nextKey
      );
      setSerializedProducts(serializedResult.rows);
      setNonSerializedProducts(nonSerializedResult.rows);
    } catch (err) {
      console.error("Unable to load order products for tracking:", err);
      message.error(
        err?.response?.data?.data || err?.message || "Unable to load products"
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !orderData?.ID) return;
    setTrackingNumber(orderData?.TrackingNumber ?? "");
    setProductShipDate(null);
    setNotifyUsers(false);
    setInvoiceId(null);
    setRequiredTissueErrors({});
    setLocalDuplicateErrors({});
    setServerTissueErrors({});
    setHasPendingTissueChecks(false);
    Object.values(scheduledChecksRef.current).forEach((scheduledCheck) => {
      clearTimeout(scheduledCheck.timeoutId);
      scheduledCheck.resolve();
    });
    pendingChecksRef.current.clear();
    scheduledChecksRef.current = {};
    latestNormalizedTissueIdsRef.current = {};
    tissueDuplicateCheckCacheRef.current.clear();
    loadProducts();
  }, [isOpen, orderData?.ID]);

  useEffect(() => {
    setLocalDuplicateErrors(checkLocalDuplicates(serializedProducts));
  }, [serializedProducts]);

  useEffect(() => {
    return () => {
      Object.values(scheduledChecksRef.current).forEach((scheduledCheck) => {
        clearTimeout(scheduledCheck.timeoutId);
        scheduledCheck.resolve();
      });
      pendingChecksRef.current.clear();
      scheduledChecksRef.current = {};
    };
  }, []);

  const calculateInvoiceTotal = () => {};

  const applyProductShipDate = (date) => {
    setProductShipDate(date);
    setSerializedProducts((prev) =>
      prev.map((row) => ({ ...row, ShipDate: date }))
    );
    setNonSerializedProducts((prev) =>
      prev.map((row) => ({ ...row, ShipDate: date }))
    );
  };

  const buildProductPayload = () =>
    [...serializedProducts, ...nonSerializedProducts].map((row) => ({
      orderProductId: row.OrderProductID,
      invoiceProductId: row.InvoiceProductID,
      productId: row.ProductID,
      productCode: row.ProductCode,
      productName: row.ProductName,
      tissueID: row.TissueID,
      price: row.Price,
      shipDate: row.ShipDate,
      expirationDate: row.ExpirationDate,
      isUsed: row.IsUsed,
      serialized: row.Serialized,
      lineIndex: row.LineIndex,
    }));

  const submitTracking = async () => {
    const hasTrackingNumber = trackingNumber.trim().length > 0;
    const hasShipDate = Boolean(productShipDate);
    const allProducts = [...serializedProducts, ...nonSerializedProducts];
    const productRowsValid =
      allProducts.length > 0 &&
      allProducts.every(
        (row) =>
          isPositiveId(row.OrderProductID) &&
          row.Price !== null &&
          row.Price !== undefined &&
          row.Price !== "" &&
          row.ShipDate
      );

    if (!hasTrackingNumber || !hasShipDate || !productRowsValid) {
      message.error("Please fill all the required fields");
      return;
    }

    const tissueIdsValid = await validateTissueIdsBeforeSubmit();
    if (!tissueIdsValid) {
      Swal.fire({
        icon: "error",
        title: "Invalid Tissue IDs",
        text: "Please fix Tissue ID errors before adding tracking.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiServ.addDataToBackend(
        {
          orderType,
          orderId: orderData.ID,
          trackingNumber: trackingNumber.trim(),
          productShipDate,
          notifyUsers,
          products: buildProductPayload(),
        },
        "addordertracking"
      );

      if (response?.status === 200 || response?.status === 201) {
        const warning = response?.data?.warning;
        Swal.fire({
          icon: warning ? "warning" : "success",
          title: warning ? "Tracking Added" : "Tracking Added Successfully",
          text: warning || undefined,
          timer: warning ? undefined : 1500,
        }).then(() => {
          onClose?.();
          window.location.reload();
        });
        return;
      }

      throw new Error("Unable to add tracking");
    } catch (err) {
      console.error("Unable to add tracking:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.data ||
          err?.response?.data?.message ||
          "Unable to add tracking",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalView
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading || isSubmitting}
      modalTitle={`Add Tracking to ${orderData?.PONumber} - ${orderData?.OrderType}`}
      textInputComponent={
        <>
          <div className="field-group-wrapper">
            <div className="field-group">
              <TextInput
                titleText="Tracking Number"
                placeholder="Tracking Number"
                isRequired
                value={trackingNumber}
                setValue={setTrackingNumber}
              />
              <DatePickerComponent
                format="MM-DD-YYYY"
                titleText="Product Ship Date"
                isRequired
                value={productShipDate}
                onChange={applyProductShipDate}
                helperText="This ship date will be applied to all products in this order"
              />
            </div>
          </div>
          <InvoiceProductDetailsSection
            orderType={orderType}
            serializedProducts={serializedProducts}
            nonSerializedProducts={nonSerializedProducts}
            setSerializedProducts={setSerializedProducts}
            setNonSerializedProducts={setNonSerializedProducts}
            calculateInvoiceTotal={calculateInvoiceTotal}
            combinedTissueErrors={combinedTissueErrors}
            handleInputChange={handleInputChange}
            shipDateDisabled
            costDisabled
            showTissueId
            tissueIdDisabled={false}
            showExpirationDate={false}
          />
          <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
            <Checkbox
              checked={notifyUsers}
              onChange={(event) =>
                setNotifyUsers(event?.target?.checked === true)
              }
            />
            <Text variant="smallText">Notify associated users via email.</Text>
          </div>
        </>
      }
      footerButtonWrapper={
        <div className="footer-button-wrapper-with-margin-top">
          <CommonButton
            variant="table"
            subVariant="success"
            onClick={submitTracking}
            disabled={isSubmitting || hasPendingTissueChecks}
          >
            Add Tracking
          </CommonButton>
        </div>
      }
    />
  );
};

export default AddTrackingModal;
